<template>
  <div class="edit-page ghost-button-styles" v-loading="pageLoading">
    <!--  组件化创意 -->
    <ComponentBasedCreative
      v-if="creativeFormGroups.length"
      ref="creative"
      :groups="creativeFormGroups"
      :group.sync="creativeFormGroups[creativeGroupIndex]"
      :creativeGroupIndex="creativeGroupIndex"
      :material-package-id="innerMaterialPackageId"
      :material-tag-list="materialTagList"
    >
    </ComponentBasedCreative>
    <div class="submit-row">
      <el-button type="primary" @click="onPlanUpdateSubmit" :loading="submitLoading">确认提交</el-button>
    </div>
  </div>
</template>

<script>
import ComponentBasedCreative from './components/creative/component-based-creative/index.vue'
import { getTencentV3CreativeInfo, getTencentV3AdInfo, updateTencentV3Creative } from '@/api/tencentada.model.js'
import { queryImageDetailByIds } from '@/api/common.model'
import { getAllMediaTagList } from '@/api/material.model.js'
import { dataFormatter } from '@/components/material-selector/utils.js'
import { getV3UpdatePlanForm, getV3UpdateCreativeForm } from '../model/project-form.js'
import { projectComputedMethods } from '../model/project-mixins.js'
import { makeV3CreativeParams } from '../model/distribute-form.js'
import { MEDIAS } from '@/common/js/constants.js'

/**
 * @description: 腾讯广告3.0创意编辑页
 * @description: https://tiangong.caohua.com/create/tencentv3/creative-edit?adId=744146812&projectId=15814345515&accountId=18122
 * @return {*}
 */
export default {
  components: { ComponentBasedCreative },
  provide() {
    return {
      creativeInstance: this
    }
  },
  data() {
    return {
      planForm: {},
      creativeInfo: null,
      submitLoading: false,
      pageLoading: false,
      materialPackageId: null,
      innerMaterialPackageId: null,
      creativeParams: {},
      creativeGroupIndex: 0,
      creativeFormGroups: [],
      materialTagList: []
    }
  },
  computed: {
    // pageStates
    ...projectComputedMethods
  },
  created() {
    this.getCreativeInfo()
  },
  methods: {
    queryMaterialTags() {
      getAllMediaTagList({ mediaId: MEDIAS.GDT }).then((data) => {
        this.materialTagList = data
        // 将媒体素材id转换成内部的素材id
        const { tagId } = data.find((item) => item.mediaTagId === String(this.materialPackageId)) || {}
        this.innerMaterialPackageId = tagId || ''
      })
    },
    /**
     * @description: 轮询获取组图占位项缩略图
     * @return {*}
     */
    initQueryGroupImageThumbnailLoop() {
      setInterval(async () => {
        const groups = this.creativeFormGroups
        for (let i = 0; i < groups.length; i++) {
          const group = groups[i]
          const { image_list } = group.selectedAssets || {}

          if (image_list?.length) {
            // 获取组图占位项id
            const ids = image_list.filter((img) => img.placeholder === true).map((img) => img.id)
            if (ids.length) {
              // 获取缩略图详情
              let imageLists = await queryImageDetailByIds({ imageDetailIds: ids })
              imageLists = imageLists.map((item) => item.items[0])
              for (let j = 0; j < imageLists.length; j++) {
                // 如果没有缩略图地址，跳过，等待下次轮询
                if (!imageLists[j]?.overview_addr) continue
                const image = dataFormatter.group_image(imageLists[j])
                const index = image_list.findIndex((img) => Number(img.id) === image.id)
                // 替换占位项内容
                if (index > -1) {
                  group.selectedAssets.image_list[index] = { ...image }
                }
              }
            }
          }
        }
        // 先进行一次清空，否则v-for不会渲染
        this.creativeFormGroups = this.creativeFormGroups.map((i) => ({ ...i, selectedAssets: {} }))
        this.$nextTick(() => {
          this.creativeFormGroups = groups
        })
      }, 5000)
    },

    /**
     * @description: 获取计划、创意信息
     */
    async getCreativeInfo() {
      try {
        this.pageLoading = true
        const { accountId, adId, projectId } = this.$route.query || {}
        // 获取计划信息
        const adInfo = await getTencentV3AdInfo({ accountId, projectId })
        this.materialPackageId = adInfo.out_plan_info.material_package_id || null
        const planForm = getV3UpdatePlanForm(adInfo)
        this.planForm = planForm

        // 等待获取到materialPackageId后再进行请求
        this.queryMaterialTags()

        // 获取创意信息
        const creativeInfo = await getTencentV3CreativeInfo({ accountId, adId })
        this.creativeInfo = creativeInfo
        const creativeFormGroups = await getV3UpdateCreativeForm({ ...creativeInfo, platform: adInfo.plan_info.platform })
        this.creativeFormGroups = creativeFormGroups

        this.creativeParams = {
          adId,
          accountId,
          projectId,
          planId: adInfo.plan_info.planId,
          materialDeriveId: creativeInfo.creative_info?.program_creative_info?.material_derive_id || ''
        }
        this.pageLoading = false
        this.initQueryGroupImageThumbnailLoop()
      } catch (e) {
        console.log('getCreativeInfo error output: ', e)
        this.pageLoading = false
      }
    },
    async onPlanUpdateSubmit() {
      const hasPlaceholderItem = this.creativeFormGroups.some((group) => {
        const { image_list } = group.selectedAssets || {}
        return image_list?.some((img) => img.placeholder === true)
      })

      if (hasPlaceholderItem) return this.$Message.warning('请等待组图生成缩略图')

      try {
        await this.$refs.creative.formValidate()
      } catch (e) {
        return
      }

      const { projectId, accountId, planId, materialDeriveId } = this.creativeParams
      let params
      try {
        params = {
          ...makeV3CreativeParams({ creative: this.creativeFormGroups[this.creativeGroupIndex], plan: this.planForm }, 0),
          dynamic_creative_id: this.creativeInfo.dynamic_creative_id,
          material_package_id: this.materialPackageId || undefined,
          adgroup_id: projectId,
          planId,
          material_derive_id: materialDeriveId,

          // 为后端标记是否智投广告
          ad_marketing_scene: this.planForm.ad_marketing_scene
        }
      } catch (e) {
        return this.$Message.error(e.message)
      }

      this.submitLoading = true
      updateTencentV3Creative({ ...params, account_id: accountId })
        .then((data) => {
          if (data) {
            this.$Message.success('提交成功，2秒后自动关闭页面')
            setTimeout(() => {
              window.close()
            }, 2000)
          }
        })
        .finally(() => {
          this.submitLoading = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-page {
  min-height: 600px;
}
.submit-row {
  width: 100%;
  position: sticky;
  bottom: 0;
  z-index: 10;
  border: 1px solid #eaeaea;
  text-align: right;
  padding: 12px;
  background-color: #fff;
}
</style>
